import { t } from 'i18next'
import { Card } from 'primereact/card'
import { Chart } from 'primereact/chart'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { ToggleButton } from 'primereact/togglebutton'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ModuleStatistic } from './ModuleStatistic'
import { API_APPRENTICESHIP_MODULES } from '../../../../constants'
import {
  ApprenticeModuleGrade,
  ApprenticeModuleGradeView,
  TimelineEnum,
} from '../../../../model/apprenticeModuleGrade.model'
import { ApprenticeshipModule } from '../../../../model/apprenticeshipModule.model'
import { RequestMethod } from '../../../../model/enums/enums'
import { Group } from '../../../../model/group.model'
import { Apprentice } from '../../../../model/person.model'
import {
  addModules,
  removeModules,
} from '../../../../redux/basic/grades/apprenticeship-module/moduleActions'
import { useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { findApprenticeById } from '../../../../util/arraySearch'
import { getActiveSemesterOfApprentice } from '../../../../util/getActiveSemester'
import { BigApprenticeAvatarWithName } from '../../../util/AvatarWithName'
import { getGradeBadge, getModuleKey } from '../util/DatatableBodies'

export const GradeStatisticView = () => {
  const { loadedBasicInformation } = useAppSelector((state) => state.user)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasLoadedAll, setHasLoadedAll] = useState<boolean>(false)
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
  const [selectedSemester, setSelectedSemester] = useState<number>()

  const [showBar, setShowBar] = useState<boolean>(true)

  const [filteredApprentices, setFilteredApprentices] = useState<Apprentice[]>(
    []
  )

  const [allApprenticeshipModuleGrades, setAllApprenticeshipModuleGrades] =
    useState<ApprenticeModuleGradeView[]>([])
  const [filteredAmg, setFilteredAMg] = useState<ApprenticeModuleGradeView[]>(
    allApprenticeshipModuleGrades
  )

  const apprentices = useAppSelector((state) => state.apprentices)

  const groups: Group[] = useAppSelector((state) => state.groups)

  // Filter use effect on group id change
  useEffect(() => {
    if (!selectedGroupIds || selectedGroupIds.length <= 0) {
      setFilteredApprentices(apprentices)

      if (selectedSemester > 0) {
        setFilteredAMg(
          allApprenticeshipModuleGrades.filter(
            (amg) => amg.semester === selectedSemester
          )
        )
        return
      }
      setFilteredAMg(allApprenticeshipModuleGrades)
      return
    }

    // Filter the groups based on the given groupIds and remove duplicates
    const filteredGroups: Group[] = []
    const processedGroupIds: Set<number> = new Set()

    groups?.forEach((group) => {
      if (
        selectedGroupIds.includes(group.group_id) &&
        !processedGroupIds.has(group.group_id)
      ) {
        filteredGroups.push(group)
        processedGroupIds.add(group.group_id)
      }
    })

    // Extract person_ids from the members' arrays of the filtered groups
    const personIds: number[] = []
    filteredGroups?.forEach((group) => {
      group.group_member?.forEach((member) => {
        personIds.push(member.id_person)
      })
    })

    // Filter apprentices based on the personIds array
    const matchedApprentices: Apprentice[] = apprentices.filter((apprentice) =>
      personIds.includes(apprentice.person_id)
    )
    setFilteredApprentices(matchedApprentices)

    const matchedAmgs: ApprenticeModuleGradeView[] =
      allApprenticeshipModuleGrades.filter(
        (amg) =>
          personIds.includes(amg.apprentice.person_id) &&
          amg.semester === selectedSemester
      )
    setFilteredAMg(matchedAmgs)
  }, [selectedGroupIds, selectedSemester])

  const apprenticeshipModules = useAppSelector(
    (state) => state.apprenticeshipModules
  )
  const gradeSchemas = useAppSelector((state) => state.gradeSchemas)

  const [lightOptions] = useState({
    plugins: {
      legend: {
        labels: {
          color: '#495057',
        },
      },
    },
    scales: {
      r: {
        min: 1,
        max: 6,
        pointLabels: {
          color: '#495057',
        },
        grid: {
          color: '#ebedef',
        },
        ticks: {
          display: true,
          stepSize: 1,
        },
        angleLines: {
          color: '#ebedef',
        },
      },
    },
  })

  const calculateAverage = (array: number[]): number => {
    if (!array || array.length === 0) {
      return -1
    }
    const sum = array?.reduce((a: number, b: number): number => a + b)
    return sum / array.length
  }

  const dispatcher = useDispatch()

  useEffect(() => {
    if (!loadedBasicInformation || isLoading) {
      return
    }

    async function loadGrades(): Promise<void> {
      setIsLoading(true)

      if (apprenticeshipModules.length < 1) {
        const aMs = await handleRequest<ApprenticeshipModule[]>(
          API_APPRENTICESHIP_MODULES(),
          RequestMethod.GET
        )
        if (aMs.data) {
          dispatcher(removeModules())
          dispatcher(addModules(aMs.data))
        }
      }

      const apprenticeshipModuleGradesResult = await handleRequest<any[]>(
        'apprentice_module_grades',
        RequestMethod.GET
      )

      const splitList = (grades: ApprenticeModuleGrade[]) => {
        const tempAllGrades: ApprenticeModuleGradeView[] = []
        grades?.forEach((grade: ApprenticeModuleGrade) => {
          const apprentice = findApprenticeById(
            grade.id_apprentice,
            apprentices
          )
          if (apprentice && apprentice.is_active === 1) {
            const tempGrade = grade as ApprenticeModuleGradeView
            tempGrade.apprentice = apprentice
            const activeSemester = getActiveSemesterOfApprentice(apprentice)
            if (activeSemester === grade.semester) {
              tempGrade.timeline_status = TimelineEnum.present
            } else if (activeSemester > grade.semester) {
              tempGrade.timeline_status = TimelineEnum.past
            } else if (activeSemester < grade.semester) {
              tempGrade.timeline_status = TimelineEnum.future
            }
            tempAllGrades.push(tempGrade)
          }
        })

        setAllApprenticeshipModuleGrades(tempAllGrades)
        setIsLoading(false)
        setHasLoadedAll(true)
      }

      const grades = apprenticeshipModuleGradesResult?.data
      if (grades && grades.length > 0) {
        splitList(grades)
      } else {
        setIsLoading(false)
      }
    }

    loadGrades()
  }, [loadedBasicInformation])

  const getAllCurrentModuleGradesOfApprentice = (
    apprenticeId: number | undefined
  ) => {
    if (!apprenticeId) {
      console.log('apprentice id not defined')
      return []
    }
    const x = filteredAmg.filter(
      (aamg: ApprenticeModuleGradeView) =>
        aamg.apprentice?.apprentice?.apprentice_id === apprenticeId
    )
    return x
  }

  const getAverageOfApprenticeGrades = (amg: ApprenticeModuleGradeView[]) => {
    const averageGradesArray = []

    const amgsByGradeSchemaSemesterAndModuleId: {
      amgId: number
      amgs: ApprenticeModuleGradeView[]
    }[] = []

    amg?.forEach((a) => {
      const temp = filteredAmg.filter(
        (aamg) =>
          aamg.id_apprenticeship_module === a.id_apprenticeship_module &&
          aamg.module_grade.id_grade_schema === 2 &&
          aamg.module_grade.grade_value
      )
      if (temp) {
        const obj = { amgId: temp[0]?.id_apprenticeship_module, amgs: temp }

        amgsByGradeSchemaSemesterAndModuleId.push(obj)
      }
    })

    amgsByGradeSchemaSemesterAndModuleId.map((b) => {
      const vals: number[] = []
      b.amgs.map((c) => {
        let v = parseFloat(c.module_grade.grade_value)
        if (v) {
          vals.push(v)
        }
      })
      const avg = calculateAverage(vals)
      averageGradesArray.push(avg > 0 ? avg : undefined)
    })

    return {
      label: t<string>('core.statistic.average'),
      backgroundColor: 'rgba(99,110,124,0.5)',
      borderColor: 'rgba(121,121,121,0.5)',
      pointBackgroundColor: 'rgba(179,181,198,5)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(179,181,198,1)',
      data: averageGradesArray,
    }
  }

  const getApprenticeRadar = (
    apprentice: Apprentice | undefined,
    allCurrentGradesOfApprentice: ApprenticeModuleGradeView[]
  ) => {
    const apprenticeId = apprentice?.apprentice?.apprentice_id

    if (!apprenticeId) {
      return undefined
    }

    const averageDataSet = getAverageOfApprenticeGrades(
      allCurrentGradesOfApprentice
    )

    return {
      labels: allCurrentGradesOfApprentice.map((aom) =>
        getModuleKey(aom, apprenticeshipModules)
      ),
      datasets: [
        {
          label: apprentice?.full_name,
          backgroundColor: 'rgba(117,190,248,0.35)',
          borderColor: 'rgba(15,122,206,0.5)',
          pointBackgroundColor: 'rgba(63,82,245,0.5)',
          pointBorderColor: 'rgba(15,122,206,0.8)',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(179,181,198,1)',
          data: allCurrentGradesOfApprentice.map(
            (aom) => aom?.module_grade?.grade_value
          ),
        },
        averageDataSet,
        {
          label: t<string>('core.statistic.sufficient'),
          backgroundColor: 'rgba(224,151,158,0.06)',
          borderColor: 'rgba(224,151,158,0.91)',
          pointBackgroundColor: 'rgba(224,151,158,0.91)',
          pointBorderColor: 'rgba(224,151,158,0.91)',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(224,151,158,1)',
          data: allCurrentGradesOfApprentice.map(() => 4),
        },
      ],
    }
  }
  const ApprenticeGradeStatisticContent = (
    apprentice: Apprentice | undefined,
    index: number
  ) => {
    const apprenticeId = apprentice?.apprentice?.apprentice_id
    if (!apprenticeId) {
      return undefined
    }

    const allCurrentGradesOfApprentice =
      getAllCurrentModuleGradesOfApprentice(apprenticeId)

    if (allCurrentGradesOfApprentice.length <= 0) {
      return undefined
    }

    return (
      <div
        key={`${index}_appr-card`}
        className={'col-12 md:col-6 lg:col-4 gap-2'}
        style={{ minWidth: '800px' }}
      >
        <Card className={'card-hover'}>
          <div className={'grid flex-wrap '}>
            <div
              className={
                'col-7 grid p-0 m-0 align-items-center justify-content-end justify-space-between'
              }
            >
              <div
                className={
                  'col-12 grid align-items-start justify-content-start'
                }
              >
                <b>
                  <BigApprenticeAvatarWithName apprentice={apprentice} />
                </b>
              </div>

              {allCurrentGradesOfApprentice.map(
                (g: ApprenticeModuleGradeView) => {
                  if (g) {
                    return (
                      <div
                        key={`gradegrid_${g.apprentice_module_grade_id}_aid${apprentice.person_id}`}
                        className={
                          'col-6 grid justify-content-start align-items-center p-0 m-0 gap-1'
                        }
                      >
                        <span className={'col-6'}>
                          {getModuleKey(
                            g as ApprenticeModuleGrade,
                            apprenticeshipModules
                          )}
                        </span>
                        <span>{getGradeBadge(g, gradeSchemas)}</span>
                      </div>
                    )
                  }
                }
              )}
            </div>
            <div
              className={'col-5 flex justify-content-center align-items-center'}
            >
              <Chart
                type="radar"
                data={getApprenticeRadar(
                  apprentice,
                  allCurrentGradesOfApprentice
                )}
                options={lightOptions}
                style={{
                  position: 'relative',
                  maxWidth: '400px',
                  width: '80%',
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const groupSelect = (group: Group) => {
    return (
      <div key={`group-item-${group.group_id}`}>
        {group.group_name}
        <span className={'small-text ml-2 secondary-text'}>
          ({group.group_description})
        </span>
      </div>
    )
  }

  return (
    <div>
      <ToggleButton
        checked={showBar}
        type={'button'}
        className={'ml-0'}
        onChange={() => setShowBar(!showBar)}
        onLabel={t<string>('core.statistic.grades.showDetail')}
        offLabel={t<string>('core.statistic.grades.showOverview')}
      />

      {hasLoadedAll && !showBar && (
        <div>
          <div className={'formgrid grid flex-wrap mt-2 mr-2 '}>
            <div className={'field col-12 lg:col-6 align-items-center '}>
              <label>{t<string>('hrm.group.plural')}</label>
              <MultiSelect
                options={groups}
                onChange={(v) => {
                  setSelectedGroupIds(v.value)
                }}
                key={'group_id'}
                optionValue={'group_id'}
                optionLabel={'group_name'}
                itemTemplate={(r) => groupSelect(r)}
                value={selectedGroupIds}
                className={'w-full'}
                placeholder={t('core.all')}
                type={'button'}
                filter
                display={'comma'}
                showClear={
                  selectedGroupIds !== undefined &&
                  selectedGroupIds?.length !== 0
                }
                filterBy="group_name"
              />
            </div>

            <div className={'field col-12 lg:col-2 align-items-center'}>
              <label>{t<string>('core.education.semester')}</label>
              <Dropdown
                options={[1, 2, 3, 4, 5, 6, 7, 8]}
                onChange={(v) => {
                  setSelectedSemester(v.value)
                }}
                value={selectedSemester}
                placeholder={t<string>('core.select')}
                type={'button'}
                className={'w-full'}
                showClear={
                  selectedSemester !== undefined && selectedSemester > 0
                }
              />
            </div>
            {/*<div className={'field col-12 lg:col-3 align-items-center'}>*/}
            {/*  <label>{t<string>('core.apprentice.neutral')}</label>*/}
            {/*  <Dropdown*/}
            {/*    options={apprentices}*/}
            {/*    valueTemplate={(p) =>*/}
            {/*      p ? (*/}
            {/*        <AvatarWithNameForDropdown person={p} />*/}
            {/*      ) : (*/}
            {/*        t<string>('core.select')*/}
            {/*      )*/}
            {/*    }*/}
            {/*    itemTemplate={(p) => <AvatarWithNameForDropdown person={p} />}*/}
            {/*    onChange={(v) => {*/}
            {/*      setSelectedPersonId(v.value)*/}
            {/*    }}*/}
            {/*    optionValue={'person_id'}*/}
            {/*    optionLabel={'full_name'}*/}
            {/*    value={selectedPersonId}*/}
            {/*    placeholder={t<string>('core.select')}*/}
            {/*    className={'w-full'}*/}
            {/*    type={'button'}*/}
            {/*    filter*/}
            {/*    showFilterClear*/}
            {/*    showClear={*/}
            {/*      selectedPersonId !== undefined && selectedPersonId > 0*/}
            {/*    }*/}
            {/*    filterBy="full_name"*/}
            {/*  />*/}
            {/*</div>*/}
          </div>
          <div className={'w-full grid flex-wrap p-0 mt-2'}>
            {filteredApprentices?.map((apr: Apprentice, index: number) => {
              if (
                loadedBasicInformation &&
                apr?.apprentice?.apprentice_id &&
                apr?.is_active === 1
              ) {
                return ApprenticeGradeStatisticContent(apr, index)
              }
            })}
          </div>
        </div>
      )}
      {hasLoadedAll && showBar && (
        <ModuleStatistic moduleGrades={allApprenticeshipModuleGrades} />
      )}
    </div>
  )
}